<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type CellValueChangedEvent,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterAircraftTypesStore} from "@/charter/stores/charter-aircraft-types-store.ts";
import {AircraftType, AircraftTypeCreateRequest, AircraftTypeUpdateRequest} from "@/api/client/models/index.ts";

const aircraftTypesStore = useCharterAircraftTypesStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
    filter: true,
  },
  columnDefs: [
    { field: "manufacturor", headerName: "Manufacturer", editable: true },
    { field: "type", headerName: "Type", editable: true },
    {
      field: "seats",
      headerName: "Seats",
      editable: true,
      valueParser: params => params.newValue === "" ? null : Number(params.newValue),
    },
    { field: "isActive", headerName: "Active" },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);
const showAddForm = ref(false);
const newAircraftType = ref<AircraftTypeCreateRequest>({
  type: "",
  manufacturor: "",
  seats: 0,
  isActive: true
});
const isSubmitting = ref(false);
const formError = ref<string | null>(null);
const gridError = ref<string | null>(null);

onMounted(async () => {
  await aircraftTypesStore.fetchAircraftTypes();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

async function onCellValueChanged(event: CellValueChangedEvent<AircraftType>) {
  const field = event.colDef.field;

  if (!field || event.newValue === event.oldValue || !event.data?.id) {
    return;
  }

  const aircraftTypeUpdateRequest: AircraftTypeUpdateRequest = {
    id: event.data.id,
    isActive: event.data.isActive,
    manufacturor: event.data.manufacturor,
    seats: event.data.seats == null ? null : Number(event.data.seats),
    type: event.data.type,
    version: event.data.version,
  };

  try {
    gridError.value = null;
    await aircraftTypesStore.updateAircraftType(aircraftTypeUpdateRequest);
  } catch (error) {
    gridError.value = error instanceof Error ? error.message : "Failed to update aircraft type";
    await aircraftTypesStore.fetchAircraftTypes();
  }
}

async function refreshData() {
  await aircraftTypesStore.fetchAircraftTypes();
}

function toggleAddForm() {
  showAddForm.value = !showAddForm.value;
  if (showAddForm.value) {
    // Reset form when opening
    newAircraftType.value = {
      type: "",
      manufacturor: "",
      seats: 0,
      isActive: true
    };
    formError.value = null;
  }
}

async function submitNewAircraftType() {
  if (!newAircraftType.value.type || !newAircraftType.value.manufacturor || newAircraftType.value.seats <= 0) {
    formError.value = "Please fill in all required fields";
    return;
  }

  try {
    isSubmitting.value = true;
    formError.value = null;
    await aircraftTypesStore.addAircraftType(newAircraftType.value);
    // Reset form after successful submission
    newAircraftType.value = {
      type: "",
      manufacturor: "",
      seats: 0,
      isActive: true
    };
    // Close the form
    showAddForm.value = false;
  } catch (error) {
    formError.value = error instanceof Error ? error.message : "Failed to add aircraft type";
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2>Aircraft Types</h2>
      <div class="flex gap-2">
        <p-button label="Add Aircraft Type" icon="pi pi-plus" severity="primary" outlined @click="toggleAddForm" />
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" outlined @click="refreshData" />
      </div>
    </div>

    <!-- Add Aircraft Type Form -->
    <div v-if="showAddForm" class="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h3 class="mb-3 mt-0">Add New Aircraft Type</h3>
      <div v-if="formError" class="mb-3 rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">{{ formError }}</div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div class="space-y-2">
          <label for="type" class="block mb-2">Type*</label>
          <p-input-text id="type" v-model="newAircraftType.type" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="manufacturor" class="block mb-2">Manufacturer*</label>
          <p-input-text id="manufacturor" v-model="newAircraftType.manufacturor" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="seats" class="block mb-2">Seats*</label>
          <p-input-number id="seats" v-model="newAircraftType.seats" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="isActive" class="block mb-2">Active</label>
          <p-checkbox id="isActive" v-model="newAircraftType.isActive" :binary="true" :disabled="isSubmitting" />
        </div>
      </div>
      <div class="mt-3 flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" outlined @click="toggleAddForm" :disabled="isSubmitting" />
        <p-button label="Save" icon="pi pi-save" @click="submitNewAircraftType" :loading="isSubmitting" />
      </div>
    </div>

    <div v-if="gridError" class="rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">
      {{ gridError }}
    </div>

    <AgGridVue
      :rowData="aircraftTypesStore.aircraftTypes"
      :loading="aircraftTypesStore.isLoading"
      :gridOptions="gridOptions"
      :theme="myTheme"
      class="ag-grid-container"
      @cell-value-changed="onCellValueChanged"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.ag-grid-container {
  width: 100%;
  height: 400px;
}

h2, h3 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

label {
  font-weight: 500;
  color: var(--text-color-secondary);
}
</style>
